import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '../common/Button'
import { SnapshotDetail } from './SnapshotDetail'

interface TimelineSnapshot {
  _id: Id<'medicationSnapshots'>
  timestamp: number
  changeDescription: string
  changeType: string
}

interface MedicationHistoryProps {
  lovedOneId: Id<'lovedOnes'>
  onClose: () => void
}

export function MedicationHistory({ lovedOneId, onClose }: MedicationHistoryProps) {
  const { t } = useTranslation()
  const [cursor, setCursor] = useState<number | undefined>()
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<Id<'medicationSnapshots'> | null>(
    null
  )

  const timeline = useQuery(api.medicationSnapshots.getTimeline, {
    lovedOneId,
    limit: 20,
    cursor,
  })

  const selectedSnapshot = useQuery(
    api.medicationSnapshots.getSnapshot,
    selectedSnapshotId ? { snapshotId: selectedSnapshotId } : 'skip'
  )

  if (selectedSnapshot) {
    return <SnapshotDetail snapshot={selectedSnapshot} onBack={() => setSelectedSnapshotId(null)} />
  }

  const containerStyles: React.CSSProperties = {
    padding: '0',
  }

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  }

  const backButtonStyles: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    padding: 0,
  }

  const titleStyles: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
  }

  const timelineStyles: React.CSSProperties = {
    position: 'relative',
    paddingLeft: '2rem',
  }

  const timelineLineStyles: React.CSSProperties = {
    position: 'absolute',
    left: '7px',
    top: '0',
    bottom: '0',
    width: '2px',
    backgroundColor: '#e5e7eb',
  }

  const entryStyles: React.CSSProperties = {
    position: 'relative',
    marginBottom: '1.5rem',
    cursor: 'pointer',
  }

  const dotStyles = (changeType: string): React.CSSProperties => {
    const colors: Record<string, string> = {
      added: '#10b981',
      edited: '#3b82f6',
      removed: '#ef4444',
      baseline: '#8b5cf6',
    }
    return {
      position: 'absolute',
      left: '-2rem',
      top: '4px',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: colors[changeType] || '#9ca3af',
      border: '2px solid #fff',
      boxShadow: '0 0 0 2px ' + (colors[changeType] || '#9ca3af'),
    }
  }

  const entryCardStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #f3f4f6',
  }

  const entryDateStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0.25rem',
  }

  const entryDescStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#1a1a1a',
  }

  const entryLinkStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#2563eb',
    marginTop: '0.25rem',
  }

  const emptyStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#9ca3af',
    fontSize: '14px',
  }

  const loadMoreStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
  }

  const getChangeIcon = (changeType: string): string => {
    const icons: Record<string, string> = {
      added: '+',
      edited: '\u270E',
      removed: '\u00D7',
      baseline: '\u2605',
    }
    return icons[changeType] || '\u2022'
  }

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <button
          style={backButtonStyles}
          onClick={onClose}
          aria-label={t('medicationManagement.history.backToMedications')}
        >
          &larr;
        </button>
        <h2 style={titleStyles}>{t('medicationManagement.history.title')}</h2>
      </div>

      {timeline === undefined ? (
        <div style={emptyStyles}>{t('common.loading')}</div>
      ) : timeline.snapshots.length === 0 ? (
        <div style={emptyStyles}>{t('medicationManagement.history.noHistory')}</div>
      ) : (
        <div style={timelineStyles}>
          <div style={timelineLineStyles} />
          {timeline.snapshots.map((snapshot: TimelineSnapshot) => (
            <div
              key={snapshot._id}
              style={entryStyles}
              onClick={() => setSelectedSnapshotId(snapshot._id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedSnapshotId(snapshot._id)
                }
              }}
            >
              <div style={dotStyles(snapshot.changeType)} />
              <div style={entryCardStyles}>
                <div style={entryDateStyles}>{formatDate(snapshot.timestamp)}</div>
                <div style={entryDescStyles}>
                  <span style={{ marginRight: '0.5rem' }}>
                    {getChangeIcon(snapshot.changeType)}
                  </span>
                  {snapshot.changeDescription}
                </div>
                <div style={entryLinkStyles}>
                  {t('medicationManagement.history.viewSchedule')} &rarr;
                </div>
              </div>
            </div>
          ))}

          {timeline.hasMore && (
            <div style={loadMoreStyles}>
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  const lastSnapshot = timeline.snapshots[timeline.snapshots.length - 1]
                  setCursor(lastSnapshot.timestamp)
                }}
              >
                {t('medicationManagement.history.loadMore')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
